<template>
  <div class="bg-white rounded-lg border-2 border-gray-900 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-xs text-gray-500 uppercase tracking-wider font-medium">
        Mindfulness
      </h3>
      <button
        @click="$emit('set-goal')"
        class="text-xs text-gray-400 hover:text-gray-600">
        Set Goal
      </button>
    </div>

    <!-- Main Stats -->
    <div class="flex items-center justify-between mb-4">
      <!-- Progress Ring -->
      <div class="relative">
        <svg class="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
          <path
            class="text-gray-200"
            stroke="currentColor"
            stroke-width="3"
            fill="none"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
          <path
            class="text-gray-900 transition-all duration-500"
            stroke="currentColor"
            stroke-width="3"
            fill="none"
            :stroke-dasharray="`${progressPercent}, 100`"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
        </svg>
        <div class="absolute inset-0 flex items-center justify-center">
          <span class="text-lg font-bold">{{ todayMinutes }}</span>
        </div>
      </div>

      <!-- Streak -->
      <div class="text-right">
        <div class="flex items-center justify-end">
          <svg class="w-5 h-5 text-orange-400 mr-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 23a7.5 7.5 0 01-5.138-12.963C8.204 8.774 11.5 6.5 11 1.5c6 4 9 8 3 14 1 0 2.5 0 5-2.47.27.773.5 1.604.5 2.47A7.5 7.5 0 0112 23z" />
          </svg>
          <span class="text-2xl font-bold">{{ currentStreak }}</span>
        </div>
        <span class="text-xs text-gray-500">day streak</span>
        <div v-if="longestStreak > currentStreak" class="text-xs text-gray-400 mt-1">
          Best: {{ longestStreak }} days
        </div>
      </div>
    </div>

    <!-- Stats Grid -->
    <div class="grid grid-cols-3 gap-4 mb-4">
      <div>
        <p class="text-lg font-bold">
          {{ totalMinutes }}
        </p>
        <p class="text-xs text-gray-500">
          total min
        </p>
      </div>
      <div>
        <p class="text-lg font-bold">
          {{ sessionCount }}
        </p>
        <p class="text-xs text-gray-500">
          sessions
        </p>
      </div>
      <div>
        <p class="text-lg font-bold">
          {{ avgDuration }}
        </p>
        <p class="text-xs text-gray-500">
          avg min
        </p>
      </div>
    </div>

    <!-- Stand Hours -->
    <div v-if="standHours !== null" class="mb-4">
      <div class="flex items-center justify-between mb-1">
        <span class="text-xs text-gray-500">Stand Hours</span>
        <span class="text-xs font-medium">{{ standHours }}/12</span>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-1.5">
        <div
          class="bg-gray-900 h-1.5 rounded-full transition-all duration-500"
          :style="{ width: `${Math.min((standHours / 12) * 100, 100)}%` }" />
      </div>
    </div>

    <!-- Heatmap Preview -->
    <div v-if="heatmapData && Object.keys(heatmapData).length > 0" class="mt-4 pt-4 border-t border-gray-100">
      <p class="text-xs text-gray-500 mb-2">
        Time of Day Pattern
      </p>
      <div class="flex space-x-0.5">
        <div
          v-for="hour in 24"
          :key="hour"
          :class="[
            'flex-1 h-4 rounded-sm',
            getHeatmapColor(hour - 1)
          ]"
          :title="`${hour - 1}:00 - ${heatmapData[hour - 1] || 0} sessions`" />
      </div>
      <div class="flex justify-between mt-1">
        <span class="text-[10px] text-gray-400">12am</span>
        <span class="text-[10px] text-gray-400">12pm</span>
        <span class="text-[10px] text-gray-400">11pm</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MindfulnessCard',

  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    streak: {
      type: Object,
      default: () => ({})
    },
    goal: {
      type: Object,
      default: null
    }
  },

  emits: ['set-goal'],

  computed: {
    todayMinutes() {
      return this.data?.today_minutes || 0
    },
    totalMinutes() {
      return this.data?.total_minutes || 0
    },
    sessionCount() {
      return this.data?.session_count || 0
    },
    avgDuration() {
      return this.data?.average_session_duration || 0
    },
    standHours() {
      return this.data?.stand_hours ?? null
    },
    currentStreak() {
      return this.streak?.current_streak || 0
    },
    longestStreak() {
      return this.streak?.longest_streak || 0
    },
    goalTarget() {
      return this.goal?.target_value || 10
    },
    progressPercent() {
      if (!this.goalTarget) return 0
      return Math.min((this.todayMinutes / this.goalTarget) * 100, 100)
    },
    heatmapData() {
      return this.data?.heatmap || {}
    }
  },

  methods: {
    getHeatmapColor(hour) {
      const count = this.heatmapData[hour] || 0
      if (count === 0) return 'bg-gray-100'
      if (count <= 2) return 'bg-gray-300'
      if (count <= 5) return 'bg-gray-500'
      return 'bg-gray-900'
    }
  }
}
</script>
